import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { apiFetch } from "@/lib/api";
import { formatPrice } from "@/lib/formatPrice";

interface ShippingMethod {
  id: number;
  name: string;
  price: number;
  active: boolean;
  is_pickup: boolean;
}

export default function Shipping() {
  const queryClient = useQueryClient();

  const [newName, setNewName] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [newIsPickup, setNewIsPickup] = useState(false);

  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editPrice, setEditPrice] = useState("");
  const [editActive, setEditActive] = useState(true);
  const [editIsPickup, setEditIsPickup] = useState(false);

  const { data: methods = [], isLoading } = useQuery<ShippingMethod[]>({
    queryKey: ["admin-shipping"],
    queryFn: () => apiFetch<ShippingMethod[]>("/api/admin/shipping-methods"),
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; price: number; is_pickup: boolean }) =>
      apiFetch<ShippingMethod>("/api/admin/shipping-methods", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-shipping"] });
      setNewName("");
      setNewPrice("");
      setNewIsPickup(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: { id: number; name: string; price: number; active: boolean; is_pickup: boolean }) =>
      apiFetch<ShippingMethod>(`/api/admin/shipping-methods/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-shipping"] });
      setEditId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/api/admin/shipping-methods/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-shipping"] });
    },
  });

  function handleAdd() {
    if (!newName.trim() || !newPrice) return;
    createMutation.mutate({ name: newName.trim(), price: Math.round(parseFloat(newPrice) * 100), is_pickup: newIsPickup });
  }

  function startEdit(method: ShippingMethod) {
    setEditId(method.id);
    setEditName(method.name);
    setEditPrice((method.price / 100).toFixed(2));
    setEditActive(method.active);
    setEditIsPickup(method.is_pickup);
  }

  function handleSave() {
    if (editId === null || !editName.trim() || !editPrice) return;
    updateMutation.mutate({
      id: editId,
      name: editName.trim(),
      price: Math.round(parseFloat(editPrice) * 100),
      active: editActive,
      is_pickup: editIsPickup,
    });
  }

  function handleDelete(id: number) {
    if (!confirm("Obrisati ovaj način dostave?")) return;
    deleteMutation.mutate(id);
  }

  return (
    <div className="max-w-lg">
      <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Načini dostave</h1>

      {/* Add new */}
      <div className="space-y-2 mb-6">
        <div className="flex gap-2">
          <Input
            placeholder="Naziv"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="flex-1"
          />
          <Input
            type="number"
            placeholder="Cijena (KM)"
            step="0.01"
            value={newPrice}
            onChange={(e) => setNewPrice(e.target.value)}
            className="w-32"
          />
          <Button
            onClick={handleAdd}
            disabled={createMutation.isPending || !newName.trim() || !newPrice}
          >
            <Plus />
            Dodaj
          </Button>
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
          <Checkbox
            checked={newIsPickup}
            onCheckedChange={(c) => setNewIsPickup(c === true)}
          />
          Lično preuzimanje
        </label>
      </div>

      {/* List */}
      {isLoading ? (
        <p className="text-muted-foreground">Učitavanje...</p>
      ) : methods.length === 0 ? (
        <p className="text-muted-foreground">Nema načina dostave.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {methods.map((method) =>
            editId === method.id ? (
              <div
                key={method.id}
                className="border rounded-lg px-3 py-3 space-y-2"
              >
                <div className="flex items-center gap-2">
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    value={editPrice}
                    onChange={(e) => setEditPrice(e.target.value)}
                    className="w-24"
                  />
                  <Button size="icon-sm" variant="ghost" onClick={handleSave} disabled={updateMutation.isPending}>
                    <Check />
                  </Button>
                  <Button size="icon-sm" variant="ghost" onClick={() => setEditId(null)}>
                    <X />
                  </Button>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <Checkbox
                      checked={editActive}
                      onCheckedChange={(c) => setEditActive(c === true)}
                      className="size-3.5"
                    />
                    Aktivno
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <Checkbox
                      checked={editIsPickup}
                      onCheckedChange={(c) => setEditIsPickup(c === true)}
                      className="size-3.5"
                    />
                    Lično preuzimanje
                  </label>
                </div>
              </div>
            ) : (
              <div
                key={method.id}
                className="flex items-center gap-2 border rounded-lg px-3 py-2"
              >
                <span className="flex-1 font-medium">{method.name}</span>
                <span className="text-sm text-muted-foreground">
                  {formatPrice(method.price)} KM
                </span>
                {method.is_pickup && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700">
                    Preuzimanje
                  </span>
                )}
                <span
                  className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                    method.active
                      ? "bg-green-50 text-green-700"
                      : "bg-red-50 text-red-700"
                  }`}
                >
                  {method.active ? "Aktivno" : "Neaktivno"}
                </span>
                <Button size="icon-sm" variant="ghost" onClick={() => startEdit(method)}>
                  <Pencil />
                </Button>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  onClick={() => handleDelete(method.id)}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 />
                </Button>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}
